import { ROLES } from "./constants/base";

// Who opens a route group of App.js: the account type (token role), then the permission of the user in
// the active winery. The platform ADMIN has no bypass: it is in the groups that list it, nowhere else.
export const ACCOUNTS = {
  admin: [ROLES.ADMIN],
  // Any logged account: the home (an ADMIN is sent on to /clients by the page) and the public changelog.
  any: [ROLES.ADMIN, ROLES.CLIENT, ROLES.USER, ROLES.TANK],
  // Persons and devices of a winery: the pages the BE routes open to CLIENT, USER and TANK (reads).
  winery: [ROLES.CLIENT, ROLES.USER, ROLES.TANK],
  // Persons only: the BE routes of these pages (writes, warehouse, report, alerts) exclude the tank monitor.
  people: [ROLES.CLIENT, ROLES.USER],
  client: [ROLES.CLIENT],
};

// [resource, action] pairs, or arrays of pairs (any-of). Resource codes are the ones of the BE route of the
// API each page calls (wine2trace-server routes/api/winery/*.js), actions in the case of the /me/rbac map.
export const PERMISSIONS = {
  operationsRead: ["OPERATIONS", "read"],
  // OPERATIONS_ANALYSIS: the analyst role writes only the analysis operations (scoped grant, hidden resource).
  operationsWrite: [
    ["OPERATIONS", "write"],
    ["OPERATIONS_ANALYSIS", "write"],
  ],
  lotsRead: ["LOTS", "read"],
  lotsWrite: ["LOTS", "write"],
  tanksRead: ["TANKS", "read"],
  tanksWrite: ["TANKS", "write"],
  warehouseRead: ["WAREHOUSE", "read"],
  warehouseWrite: ["WAREHOUSE", "write"],
  // /warehouse/:type/:id/update edits a stock row, an item (WAREHOUSE) or a treatment substance (DRUGS).
  warehouseOrDrugsWrite: [
    ["WAREHOUSE", "write"],
    ["DRUGS", "write"],
  ],
  drugsWrite: ["DRUGS", "write"],
  // The Protocols page lists the analysis and treatment protocols (PROTOCOLS), the winemaking ones
  // (WORKING_PROTOCOLS: /protocols/winemaking) and the application templates (WINEMAKING_PROTOCOLS).
  protocolsList: [
    ["PROTOCOLS", "read"],
    ["WORKING_PROTOCOLS", "read"],
    ["WINEMAKING_PROTOCOLS", "read"],
  ],
  protocolsRead: ["PROTOCOLS", "read"],
  protocolsWrite: ["PROTOCOLS", "write"],
  workingProtocolsRead: ["WORKING_PROTOCOLS", "read"],
  workingProtocolsWrite: ["WORKING_PROTOCOLS", "write"],
  winemakingProtocolsWrite: ["WINEMAKING_PROTOCOLS", "write"],
  protocolApplicationsRead: ["PROTOCOLS_APPLICATIONS", "read"],
  protocolApplicationsWrite: ["PROTOCOLS_APPLICATIONS", "write"],
  suppliersRead: ["SUPPLIERS", "read"],
  suppliersWrite: ["SUPPLIERS", "write"],
  vineyardsRead: ["VINEYARDS", "read"],
  vineyardsWrite: ["VINEYARDS", "write"],
  wineriesRead: ["WINERIES", "read"],
  wineriesWrite: ["WINERIES", "write"],
  usersWrite: ["USERS", "write"],
  usersDelete: ["USERS", "delete"],
  // Role and permission management: the matrix (GET /utils/permissions) needs ROLES read, the grant, role and
  // winery assignment writes (PUT /users/update/*) need ROLES write. The BE routes are CLIENT and USER accounts only.
  rolesRead: ["ROLES", "read"],
  rolesWrite: ["ROLES", "write"],
  reportsExport: ["REPORTS", "export"],
  alertsRead: ["ALERTS", "read"],
};

// can(resource, action) of usePermissions() over a PERMISSIONS entry: a [resource, action] pair or an array of
// pairs (any-of), as the route guard of @wine2trace/auth reads it. For a button that opens a guarded route.
export const canAnyOf = (can, permission) => {
  if (!Array.isArray(permission) || permission.length === 0) return false;
  const pairs = typeof permission[0] === "string" ? [permission] : permission;
  return pairs.some(([resource, action]) => can(resource, action));
};
